import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface Props {
  children: ReactNode
  /** Optional custom fallback */
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode
  /** Used to identify the boundary in logs */
  context?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Catches render errors below it and shows a friendly recovery UI
 * instead of an unmounted-tree blank screen. Place around each route
 * so one broken page doesn't crash the whole shell.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[RouteErrorBoundary${this.props.context ? `:${this.props.context}` : ''}]`, error, info)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset })
      }
      return <DefaultErrorFallback error={this.state.error} reset={this.reset} />
    }
    return this.props.children
  }
}

function DefaultErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const isDev = import.meta.env.DEV

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="max-w-md">
        <CardContent className="space-y-4 pt-6 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
          <div>
            <h2 className="font-display text-lg font-semibold">Something went wrong</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              An error occurred while rendering this page. You can try again, or navigate elsewhere.
            </p>
          </div>
          {isDev && (
            <details className="rounded-md border border-border bg-muted/50 p-3 text-left text-xs">
              <summary className="cursor-pointer font-medium">Developer details</summary>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap font-mono text-[10px]">
                {error.name}: {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>Reload page</Button>
            <Button variant="brand" onClick={reset}>
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
